import React, { useState, useRef, useEffect } from 'react';
import useClickOutside from '../../Hooks/useClickOutside';
import { Modal } from './modal-component';
import { ButtonBlack, ButtonError } from '../Buttons/button-component';
import RecursiveField from '../Field/field-recursive-component';
import { useSelector } from 'react-redux';

const SchemaEditorModal = ({ open, onClose, schema, draft, setDraft, onSave, strings }) => {
    const { dir } = useSelector((state) => state.systemData || { dir: 'rtl' });

    const flatFields = schema.filter(f => !['object', 'array'].includes(f.type));
    const modules = schema.filter(f => ['object', 'array'].includes(f.type));

    const hasModules = modules.length > 0;
    const [activeTab, setActiveTab] = useState('general');
    const [mobileView, setMobileView] = useState('menu');

    const [rawString, setRawString] = useState("");
    const [jsonError, setJsonError] = useState(null);
    const contentRef = useRef(null);

    useClickOutside(contentRef, () => { if (open) onClose(); });

    useEffect(() => {
        if (activeTab === 'raw') {
            setRawString(JSON.stringify(draft, null, 4));
            setJsonError(null);
        }
    }, [activeTab, draft]);

    if (!open) return null;

    const applyJsonChanges = () => {
        try {
            const parsed = JSON.parse(rawString);
            const originalKeys = Object.keys(draft);
            const newKeys = Object.keys(parsed);
            const missingKeys = originalKeys.filter(key => !newKeys.includes(key));

            if (missingKeys.length > 0) {
                setJsonError(`${strings.modal.fixErrors} ${missingKeys.join(', ')}`);
                return;
            }
            setDraft(parsed);
            setJsonError(null);
        } catch (err) {
            setJsonError(strings.modal.invalidJson);
        }
    };

    const formatJson = () => {
        try {
            const parsed = JSON.parse(rawString);
            setRawString(JSON.stringify(parsed, null, 4));
            setJsonError(null);
        } catch (err) {
            setJsonError(strings.modal.invalidJson);
        }
    };

    return (
        <Modal expand={open}>
            <div ref={contentRef} dir={dir} className="w-full lg:max-w-[85vw] bg-white dark:bg-[#0c0c0c] rounded-2xl shadow-2xl flex flex-col h-[90vh] lg:h-[85vh] border border-neutral-200 dark:border-neutral-800 overflow-hidden font-primary">

                {/* --- HEADER --- */}
                <div className="px-8 py-5 border-b border-neutral-100 dark:border-neutral-800 flex justify-between items-center bg-white dark:bg-[#0c0c0c] shrink-0 z-20">
                    <div className="flex items-center gap-4">
                        {mobileView === 'content' && hasModules && (
                            <button onClick={() => setMobileView('menu')} className="lg:hidden p-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                                <svg className={`w-4 h-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" /></svg>
                            </button>
                        )}
                        <h2 className="text-xl font-bold text-neutral-900 dark:text-white ">
                            {mobileView === 'menu' ? strings.modal.title : activeTab.split(/(?=[A-Z])/).join(" ")}
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
                        <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div className="flex flex-grow overflow-hidden relative">
                    {/* --- SIDEBAR --- */}
                    {hasModules && (
                        <div className={`${mobileView === 'menu' ? 'flex' : 'hidden lg:flex'} absolute inset-0 z-10 lg:relative lg:inset-auto w-full lg:w-72 border-r border-neutral-100 dark:border-neutral-800 bg-[#fcfcfc] dark:bg-[#0a0a0a] flex flex-col p-5 shrink-0 overflow-y-auto`}>
                            <div className="space-y-2 pb-24">
                                <span className="text-[12px] font-bold text-neutral-400 mb-4 block px-3 uppercase tracking-wider">
                                    {strings.modal.navigation}
                                </span>

                                <button
                                    onClick={() => { setActiveTab('general'); setMobileView('content'); }}
                                    className={`w-full px-5 py-3.5 rounded-xl text-left text-[15px] font-bold transition-all ${activeTab === 'general' ? 'bg-blue-600 text-white shadow-lg' : 'text-neutral-500 hover:bg-neutral-100'}`}
                                >
                                    {strings.modal.generalSettings}
                                </button>

                                {modules.map(m => (
                                    <button
                                        key={m.key}
                                        onClick={() => { setActiveTab(m.key); setMobileView('content'); }}
                                        className={`w-full px-5 py-3.5 rounded-xl text-left text-[15px] font-bold capitalize transition-all ${activeTab === m.key ? 'bg-blue-600 text-white shadow-lg' : 'text-neutral-500 hover:bg-neutral-100'}`}
                                    >
                                        {m.label.split(/(?=[A-Z])/).join(" ")}
                                    </button>
                                ))}

                                <div className="pt-6">
                                    <button
                                        onClick={() => { setActiveTab('raw'); setMobileView('content'); }}
                                        className={`w-full px-5 py-3.5 rounded-xl text-left text-xs font-black uppercase transition-all ${activeTab === 'raw' ? 'bg-neutral-900 text-white' : 'border border-neutral-200 dark:border-neutral-700 text-neutral-500 hover:bg-neutral-100'}`}
                                    >
                                        {strings.modal.jsonMode}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- MAIN WORKSPACE --- */}
                    <div className="flex-grow overflow-y-auto bg-white dark:bg-[#0c0c0c] custom-scrollbar pb-32">
                        <div className="max-w-4xl mx-auto py-10 px-6 lg:px-12">
                            {activeTab === 'general' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {flatFields.map(f => (
                                        <div key={f.key} className={['name', 'slug'].includes(f.label.toLowerCase()) ? 'md:col-span-2' : ''}>
                                            <label className="text-[13px] font-bold text-neutral-500 block mb-2 ml-1">{f.label}</label>
                                            <RecursiveField
                                                fieldKey={f.key}
                                                value={draft[f.key]}
                                                onChange={(val) => setDraft({ ...draft, [f.key]: val })}
                                                strings={strings}
                                                dir={dir}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {(activeTab !== 'general' && activeTab !== 'raw') && (
                                <RecursiveField
                                    fieldKey={activeTab}
                                    value={draft[activeTab]}
                                    onChange={(newVal) => setDraft({ ...draft, [activeTab]: newVal })}
                                    strings={strings}
                                    dir={dir}
                                />
                            )}

                            {activeTab === 'raw' && hasModules && (
                                <div className="flex flex-col h-[65vh] bg-[#1b1c1e] rounded-xl border border-neutral-800 overflow-hidden shadow-2xl">
                                    <div className="px-5 py-3 bg-[#252627] border-b border-neutral-800 flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <button onClick={applyJsonChanges} className="px-4 py-1.5 bg-blue-600 text-white text-[11px] font-bold rounded-lg hover:bg-blue-500 transition-colors">
                                                {strings.modal.doneJson}
                                            </button>
                                            <button onClick={formatJson} className="px-4 py-1.5 bg-neutral-700 text-neutral-300 text-[11px] font-bold rounded-lg hover:bg-neutral-600 transition-colors">
                                                {strings.modal.formatJson}
                                            </button>
                                        </div>
                                        {jsonError && <span className="text-[11px] text-red-400 font-bold italic">{jsonError}</span>}
                                    </div>
                                    <textarea
                                        dir="ltr"
                                        className="w-full flex-grow bg-transparent text-[#9cdcfe] font-mono text-[14px] p-8 outline-none resize-none leading-relaxed custom-scrollbar selection:bg-blue-500/20"
                                        spellCheck="false"
                                        value={rawString}
                                        onChange={(e) => setRawString(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- FOOTER --- */}
                <div className="absolute bottom-4 left-4 right-4 sm:mx-auto sm:max-w-md flex items-center justify-center gap-4 px-8 py-5 z-30 bg-white/95 dark:bg-[#0c0c0c]/95 backdrop-blur-md border border-neutral-100 dark:border-neutral-800 shadow-2xl rounded-3xl lg:static lg:bottom-auto lg:left-auto lg:right-auto lg:max-w-xl lg:w-full lg:rounded-none lg:border-t lg:border-b-0 lg:border-x-0 lg:justify-end lg:px-10 lg:py-6 lg:shadow-none lg:bg-white lg:dark:bg-[#0c0c0c] transition-all duration-300">
                    <ButtonError onClick={onClose} >
                        {strings.modal.cancel}
                    </ButtonError>
                    <ButtonBlack onClick={() => onSave(draft)}>
                        {strings.modal.save}
                    </ButtonBlack>
                </div>
            </div>
        </Modal>
    );
};

export default SchemaEditorModal;